import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, Link, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { saveBlob, filenameFromResponse } from '../freight-profitability/freightPnlUtils'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { KIND, download, errorText, fetchZoneSummary } from './ptlSetupApi'
import { NOT_SERVICEABLE, ROUTES, fmtDate, num, zoneMapTabs } from './constants'
import { Banner, ZoneDistribution } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import ZoneListing, { EMPTY_ZONE_FILTERS } from './zone-map/ZoneListing'

const filtersFrom = (params) => Object.fromEntries(Object.keys(EMPTY_ZONE_FILTERS).map((k) => [k, params.get(k) || '']))

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant Zone Map · Standard (plan screen 5). The Prozo standard PTL zone map every
 * merchant uses (B2BZoneId, cp = PROZO_B2B_Z_MAP): KPI cards, pincodes per zone, the filterable pincode listing, download,
 * and links to its Upload and History screens. Filters live in the link (?zoneId=&state=…). Merchant overrides have
 * their own screen.
 */
export default function PtlZoneMapStandard() {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [params, setParams] = useSearchParams()
  const [summary, setSummary] = useState(null)
  const [error, setError] = useState('')
  const [downloading, setDownloading] = useState(false)

  const paramText = params.toString()
  const filters = useMemo(() => filtersFrom(new URLSearchParams(paramText)), [paramText])
  const setFilters = useCallback((next) => {
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v !== '' && v != null)), { replace: true })
  }, [setParams])
  const toggle = (k, v) => setFilters({ ...filters, [k]: filters[k] === v ? '' : v })

  const load = useCallback(() => {
    setError('')
    fetchZoneSummary(null).then(setSummary).catch((e) => setError(errorText(e, 'Could not load the zone map')))
  }, [])
  useEffect(() => { load() }, [load])

  const noZones = summary && summary.zonesDefined === 0
  const latest = summary?.latest
  const standardIds = useMemo(() => new Set((summary?.zones || []).filter((z) => z.standard).map((z) => z.zoneId)), [summary])

  const doDownload = async () => {
    setDownloading(true)
    try {
      const res = await download(KIND.ZONE_MAP, null)
      saveBlob(res.data, filenameFromResponse(res, 'ptl_standard_zone_map.csv'))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed. Try again.'), { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const s = summary || {}
  const kpis = [
    { key: 'mapped', label: 'Pincodes mapped', value: num(s.pincodesMapped), sub: 'With a serviceable zone' },
    { key: 'zones', label: 'Zones in use', value: `${num(s.zonesInUse)} / ${num(s.zonesDefined)}`, sub: 'Zones of the standard TAT' },
    { key: 'ns', label: 'Not serviceable (9999)', value: num(s.notServiceable), tone: s.notServiceable ? 'danger' : 'ok',
      sub: s.notServiceable ? 'No price, no TAT' : 'None', active: filters.zoneId === NOT_SERVICEABLE,
      onClick: s.notServiceable ? () => toggle('zoneId', NOT_SERVICEABLE) : undefined },
    { key: 'oda', label: 'ODA pincodes', value: num(s.odaPincodes), sub: 'ODA 1, 2 or 3' },
    { key: 'green', label: 'Green tax pincodes', value: num(s.greenTaxPincodes), sub: 'Green tax on the drop side',
      active: filters.greenTax === 'true', onClick: () => toggle('greenTax', 'true') },
    { key: 'special', label: 'Special zone pincodes', value: num(s.specialZonePincodes), sub: 'Special zone charge',
      active: filters.specialZone === 'true', onClick: () => toggle('specialZone', 'true') },
    { key: 'overrides', label: 'Merchant overrides', value: num(s.merchantsWithOwnMap), sub: 'Merchants with their own map', to: ROUTES.zoneOverrides },
    { key: 'last', label: 'Last change', value: latest ? fmtDate(latest.createdAt).split(',')[0] : 'None', sub: latest ? latest.createdBy || '' : 'Not uploaded from this page yet' },
  ]

  return (
    <PtlPage title='Merchant Zone Map' tabTitle='Standard Zone Map' tabs={zoneMapTabs(summary ? s.merchantsWithOwnMap : undefined)}
      description='The Prozo standard PTL zone map every merchant uses. Each shipment takes its pickup and drop zone from it; the two zone ids make the lane that sets price, TAT and EDD.'
      actions={(
        <>
          <LoadingButton variant='outlined' startIcon={<DownloadOutlinedIcon />} loading={downloading} disabled={!s.pincodesMapped && !s.notServiceable}
            onClick={() => triggerDownload(doDownload, 'Standard zone map')} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            Download full map
          </LoadingButton>
          <Button variant='outlined' startIcon={<HistoryOutlinedIcon />} onClick={() => navigate(ROUTES.zoneMapHistory)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>History</Button>
          <Button variant='contained' startIcon={<UploadFileOutlinedIcon />} disabled={!summary || noZones} onClick={() => navigate(ROUTES.zoneMapUpload)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
            Upload map
          </Button>
        </>
      )}
      kpis={kpis} kpisLoading={!summary && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {noZones && (
        <Banner tone='warn'>
          No PTL zones yet. The zones come from the standard TAT, so upload it first on{' '}
          <Link component={RouterLink} to={ROUTES.tat}>Merchant TAT</Link>.
        </Banner>
      )}

      {summary?.zones?.length > 0 && (
        <Card elevation={0} sx={{ ...SX.card, mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
            <Typography component='h2' sx={TYPE.sectionTitle}>Pincodes per zone</Typography>
            <Typography sx={{ ...TYPE.rowHelp, fontSize: 12 }}>
              {filters.zoneId
                ? <>Listing zone {filters.zoneId} only. <Link component='button' onClick={() => toggle('zoneId', filters.zoneId)} sx={{ fontSize: 12, verticalAlign: 'baseline' }}>Show all zones</Link></>
                : 'Select a zone to list only its pincodes.'}
            </Typography>
          </Box>
          <ZoneDistribution
            zones={[...summary.zones, ...(summary.notServiceable > 0 ? [{ zoneId: NOT_SERVICEABLE, name: 'Not serviceable', area: 'Zone 9999: no price, no TAT', pincodes: summary.notServiceable }] : [])]}
            standardIds={standardIds} selected={filters.zoneId} onPick={(id) => toggle('zoneId', id)} />
        </Card>
      )}

      <ZoneListing merchantId={null} zones={summary?.zones} filters={filters} onFilters={setFilters} />

      <Typography sx={{ ...TYPE.rowHelp, mt: 1.5, color: C.inkMuted }}>
        A change applies to shipments priced after it is saved; existing shipments keep their zone. Merchants with their own map use it for their pincodes:{' '}
        <Link component={RouterLink} to={ROUTES.zoneOverrides}>see merchant overrides</Link>.
      </Typography>

      {PolicyDialog}
    </PtlPage>
  )
}
